import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Card, Modal } from "react-bootstrap";
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEye, faPen, faTrash, faFilter, faSync, faToggleOn, faToggleOff } from '@fortawesome/free-solid-svg-icons';
import useFetch from "../../../hooks/useFetch";
import RequireRoles from '../../../components/RequireRoles';
import PaginatedItems from '../../../components/administracao/Pagination';
import Swal from 'sweetalert2';
import Select from 'react-select';

export default function GerenciarFotografias() {
    const { params } = useParams();
    const { get, put, del } = useFetch();

    const defaultState = {
        fotografias: [],
        count: 0,
        page: 1,
        filtroAtivo: false,
        filtros: {
            numero_tombo: "",
            titulo_atribuido: "",
            decada_producao: "",
            id_acervo_detentor: ""
        },
        acervos: [],
        mostrarModalInativacao: false,
        mostrarModalExclusao: false,
        registroSelecionado: {},
        loading: true
    };

    const [state, setState] = useState(() => defaultState);

    useEffect(() => {
        async function initialize() {
            let [fotografias, count] = await buscarFotografias({});
            let acervos = await buscarAcervos();
            setState(prev => ({ ...prev, fotografias: fotografias ?? [], count: count ?? 0, acervos, loading: false }));
        }
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    async function buscarFotografias(filtros) {
        let query = "?";
        if (filtros && Object.keys(filtros).length > 0) {
            for (const key in filtros) {
                if (filtros[key] !== "") {
                    query = query.concat(key, "=", filtros[key], "&");
                }
            }
        }
        try {
            let response = await get('/fotografia' + query);
            if (response.ok) {
                let dados = response.result;
                return [dados.data, dados.count];
            }
            console.error('Erro no buscarFotografias()');
            return [[], 0];
        } catch (error) {
            console.error('Erro no buscarFotografias():', error);
            return [[], 0];
        }
    }

    // Opções do filtro por acervo (listagem é pública, não precisa de token).
    async function buscarAcervos() {
        try {
            let response = await get('/acervodetentor?page=1');
            if (response.ok) {
                return (response.result.data ?? []).map(p => ({ value: p.id_acervo_detentor, label: p.nome }));
            }
            return [];
        } catch (error) {
            console.error('Erro no buscarAcervos():', error);
            return [];
        }
    }

    async function handlePageChange(pageNumber) {
        window.scrollTo(0, 0);
        let filtros = state.filtroAtivo ? { ...state.filtros, page: pageNumber } : { page: pageNumber };
        let [fotografias, count] = await buscarFotografias(filtros);
        setState({ ...state, page: pageNumber, fotografias, count, loading: false });
    }

    async function filtrarFotografias() {
        let [fotografias, count] = await buscarFotografias(state.filtros);
        setState({ ...state, fotografias, count, filtroAtivo: true, loading: false });
    }

    async function limparFiltros() {
        let [fotografias, count] = await buscarFotografias({});
        setState({ ...state, filtros: defaultState.filtros, fotografias, count, filtroAtivo: false, loading: false });
    }

    function handleChangeFiltros(event) {
        const { name, value } = event.target;
        setState(prev => ({ ...prev, filtros: { ...prev.filtros, [name]: value } }));
    }

    // Mesmos estilos do react-select usados no formulário de Fotografia,
    // pra o filtro não destoar dos outros campos da tela.
    const selectStyles = {
        control: (base, stateProps) => ({
            ...base,
            borderRadius: '10px',
            borderColor: '#e2e8f0',
            minHeight: '44px',
            backgroundColor: stateProps.isDisabled ? '#e9ecef' : base.backgroundColor,
        }),
        menu: (base) => ({ ...base, zIndex: 5 }),
        // menuPortal: o menu é renderizado no <body> (menuPortalTarget abaixo),
        // então não é mais cortado pelo card/modal em que o Select está nem
        // aparece por baixo da navbar. z-index alinhado com scss/base/responsivo.scss.
        menuPortal: (base) => ({ ...base, zIndex: 1080 }),
        option: (base, stateProps) => ({
            ...base,
            backgroundColor: stateProps.isSelected ? '#587b7f' : stateProps.isFocused ? '#e8f0f1' : '#fff',
            color: stateProps.isSelected ? '#fff' : '#2c3e50',
        }),
        singleValue: (base) => ({ ...base, color: '#2c3e50' }),
        placeholder: (base) => ({ ...base, color: '#6c757d' }),
    };

    function confirmarInativacao(fotografia) {
        setState({ ...state, mostrarModalInativacao: true, registroSelecionado: fotografia });
    }

    function confirmarExclusao(fotografia) {
        setState({ ...state, mostrarModalExclusao: true, registroSelecionado: fotografia });
    }

    async function toggleStatus() {
        const fotografia = state.registroSelecionado;
        const rota = fotografia.status_fotografia === "ativo" ? "inativar" : "ativar";
        try {
            let response = await put(`/fotografia/${fotografia.id_fotografia}/${rota}`, {});
            if (response.status === 200) {
                let [fotografias, count] = await buscarFotografias(state.filtroAtivo ? state.filtros : {});
                setState({ ...state, fotografias, count, mostrarModalInativacao: false, registroSelecionado: {}, loading: false });
            } else {
                Swal.fire({ title: "Não foi possível alterar o status", icon: "error", confirmButtonColor: "#587b7f" });
            }
        } catch (error) {
            console.error('Erro no toggleStatus():', error);
            Swal.fire({ title: "Erro de conexão", text: "Não foi possível falar com o servidor. Verifique sua conexão e tente novamente.", icon: "error", confirmButtonColor: "#587b7f" });
        }
    }

    async function excluirFotografia() {
        const fotografia = state.registroSelecionado;
        try {
            let response = await del(`/fotografia/${fotografia.id_fotografia}`);
            if (response.status === 200) {
                let [fotografias, count] = await buscarFotografias(state.filtroAtivo ? state.filtros : {});
                setState({ ...state, fotografias, count, mostrarModalExclusao: false, registroSelecionado: {}, loading: false });
            } else {
                Swal.fire({ title: "Não foi possível excluir a fotografia", text: response.result?.message ?? "", icon: "error", confirmButtonColor: "#587b7f" });
            }
        } catch (error) {
            console.error('Erro no excluirFotografia():', error);
            Swal.fire({ title: "Erro de conexão", text: "Não foi possível falar com o servidor. Verifique sua conexão e tente novamente.", icon: "error", confirmButtonColor: "#587b7f" });
        }
    }

    return (
        <div className="admin-page-container">
            <div className="admin-header-container">
                <div className="title-group">
                    <h2 className="admin-title">Acervo Iconográfico</h2>
                    <p className="admin-subtitle">Gerencie fotografias e documentos visuais do museu</p>
                </div>
                <Link to="/admin/iconografico/fotografias/criar" className="btn-add-new">
                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Nova Fotografia
                </Link>
            </div>

            <Card className="filter-card-admin mb-4">
                <Card.Body className="p-4">
                    <Form className="row g-3">
                        <Form.Group className="col-md-4">
                            <Form.Label className="label-admin">Número de Tombo</Form.Label>
                            <Form.Control
                                className="input-moderno"
                                placeholder="Ex: FOT-001"
                                name="numero_tombo"
                                value={state.filtros.numero_tombo}
                                onChange={handleChangeFiltros}
                            />
                        </Form.Group>
                        <Form.Group className="col-md-4">
                            <Form.Label className="label-admin">Título</Form.Label>
                            <Form.Control
                                className="input-moderno"
                                placeholder="Buscar por título..."
                                name="titulo_atribuido"
                                value={state.filtros.titulo_atribuido}
                                onChange={handleChangeFiltros}
                            />
                        </Form.Group>
                        <Form.Group className="col-md-4">
                            <Form.Label className="label-admin">Acervo</Form.Label>
                            <Select menuPortalTarget={document.body}
                                options={state.acervos}
                                placeholder="Todos os acervos..."
                                styles={selectStyles}
                                isClearable
                                value={state.acervos.find(o => o.value === state.filtros.id_acervo_detentor) ?? null}
                                onChange={(op) => setState(prev => ({ ...prev, filtros: { ...prev.filtros, id_acervo_detentor: op?.value ?? "" } }))}
                            />
                        </Form.Group>
                        <Form.Group className="col-md-4">
                            <Form.Label className="label-admin">Década</Form.Label>
                            <Form.Control
                                type="number"
                                className="input-moderno"
                                placeholder="Ex: 1950"
                                name="decada_producao"
                                value={state.filtros.decada_producao}
                                onChange={handleChangeFiltros}
                            />
                        </Form.Group>
                        <div className="col-12 d-flex justify-content-between align-items-end">
                            <div className="d-flex gap-2">
                                <Button className="btn-apply-filters" onClick={filtrarFotografias}>
                                    <FontAwesomeIcon icon={faFilter} className="me-2" /> Aplicar filtros
                                </Button>
                                <Button className="btn-clear-filters" onClick={limparFiltros}>
                                    <FontAwesomeIcon icon={faSync} />
                                </Button>
                            </div>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            <Card className="table-card-admin">
                <div className="table-responsive">
                    <Table className="modern-table mb-0">
                        <thead>
                            <tr>
                                <th>Tombo</th>
                                <th>Título</th>
                                <th>Autor</th>
                                <th>Acervo</th>
                                <th>Técnica</th>
                                <th>Cor</th>
                                <th>Conservação</th>
                                <th>Status</th>
                                <th className="text-center">Opções</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.loading ? (
                                <tr><td colSpan="9" className="text-center py-5"><div className="spinner-border text-teal"></div></td></tr>
                            ) : state.fotografias.length > 0 ? state.fotografias.map((f) => (
                                <tr key={f.id_fotografia}>
                                    <td data-label="Tombo" className="fw-bold text-teal">{f.numero_tombo}</td>
                                    <td data-label="Título" className="fw-bold">{f.titulo_atribuido}</td>
                                    <td data-label="Autor" className="text-muted">{f.AutorFotografo?.nome ?? "-"}</td>
                                    <td data-label="Acervo" className="text-muted">{f.AcervoDetentor?.nome ?? "-"}</td>
                                    <td data-label="Técnica" className="small">{f.tecnica ?? "-"}</td>
                                    <td data-label="Cor"><span className="badge bg-light text-dark border">{f.cor ?? "-"}</span></td>
                                    <td data-label="Conservação" className="small">{f.estado_conservacao ?? "-"}</td>
                                    <td data-label="Status">
                                        <span className={`status-badge ${f.status_fotografia === 'ativo' ? 'active' : 'inactive'}`}>
                                            {f.status_fotografia === 'ativo' ? 'Ativo' : 'Inativo'}
                                        </span>
                                    </td>
                                    <td data-label="Opções">
                                        <div className="d-flex justify-content-center gap-2">
                                            <Link className="action-icon view" to={`/admin/iconografico/fotografias/${f.id_fotografia}`}><FontAwesomeIcon icon={faEye} /></Link>
                                            <Link className="action-icon edit" to={`/admin/iconografico/fotografias/${f.id_fotografia}`} state={{ acao: "Editar" }}><FontAwesomeIcon icon={faPen} /></Link>
                                            <button
                                                className="action-icon toggle"
                                                title={f.status_fotografia === 'ativo' ? "Inativar" : "Ativar"}
                                                onClick={() => confirmarInativacao(f)}
                                            >
                                                <FontAwesomeIcon icon={f.status_fotografia === 'ativo' ? faToggleOn : faToggleOff} />
                                            </button>
                                            <RequireRoles allowedRoles={['Desenvolvedor', 'Administrador']}>
                                                <button className="action-icon delete" onClick={() => confirmarExclusao(f)}>
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </RequireRoles>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="9" className="text-center py-4">Nenhuma fotografia encontrada.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </div>
                {state.fotografias.length > 0 && (
                    <div className="card-footer bg-white border-0 py-3 d-flex justify-content-center">
                        <PaginatedItems itemCount={state.count} itemsPerPage={20} handlePageChange={handlePageChange} />
                    </div>
                )}
            </Card>

            <Modal show={state.mostrarModalInativacao} onHide={() => setState({ ...state, mostrarModalInativacao: false })} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{state.registroSelecionado.status_fotografia === "ativo" ? "Inativar" : "Ativar"} Fotografia</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Deseja alterar o status da obra <strong>{state.registroSelecionado.titulo_atribuido}</strong> para {state.registroSelecionado.status_fotografia === "ativo" ? "Inativo" : "Ativo"}?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setState({ ...state, mostrarModalInativacao: false })}>Cancelar</Button>
                    <Button variant="primary" onClick={toggleStatus}>Confirmar</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={state.mostrarModalExclusao} onHide={() => setState({ ...state, mostrarModalExclusao: false })} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Excluir Fotografia</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Tem certeza que deseja excluir definitivamente <strong>{state.registroSelecionado.titulo_atribuido}</strong>? Essa ação não pode ser desfeita. Prefira inativar sempre que possível.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setState({ ...state, mostrarModalExclusao: false })}>Cancelar</Button>
                    <Button variant="danger" onClick={excluirFotografia}>Excluir definitivamente</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
